import { useCallback, useMemo } from 'react';
import { useAuth, ROLE_SI_RH } from '@/contexts/AuthContext';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import type { Solicitacao } from '@/features/SI/models';
import {
  useSolicitacoesDoTimeRhQuery,
  useSolicitacoesPorSolicitanteQuery,
  useTimeRhEmailsQuery,
} from '@/features/SI/hooks/data';

/**
 * "Minhas solicitações" do Portal do SI. Pra quem tem si_rh, inclui o que qualquer pessoa do
 * time RH abriu (cobrir férias/licença) — e devolve `abertaPor` pra mostrar de quem é cada
 * uma. Pros demais, é só o que a pessoa abriu.
 */
export function useMinhasSolicitacoes() {
  const { user, userHasRole } = useAuth();
  const ehRh = userHasRole(ROLE_SI_RH);
  const minhas = useSolicitacoesPorSolicitanteQuery(user?.collaboratorId);
  const timeRhEmails = useTimeRhEmailsQuery();
  const doTime = useSolicitacoesDoTimeRhQuery(ehRh ? timeRhEmails.data : undefined);
  const { data: colaboradores = [] } = useCollaboratorsPublicQuery();

  const data = useMemo(() => {
    const porId = new Map<string, Solicitacao>();
    for (const s of [...(minhas.data ?? []), ...(ehRh ? (doTime.data ?? []) : [])]) porId.set(s.id, s);
    return Array.from(porId.values());
  }, [minhas.data, doTime.data, ehRh]);

  const abertaPor = useCallback(
    (solicitacao: Solicitacao) => {
      if (solicitacao.solicitanteId === user?.collaboratorId) return null;
      return colaboradores.find((c) => c.id === solicitacao.solicitanteId)?.name ?? solicitacao.solicitanteEmail;
    },
    [colaboradores, user?.collaboratorId]
  );

  return {
    data,
    isLoading: minhas.isLoading || (ehRh && (timeRhEmails.isLoading || doTime.isLoading)),
    ehRh,
    abertaPor,
  };
}
